import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import TextInput from 'components/Common/Form/Input/TextInput'
import { useAppSelector } from 'hooks/reduxHooks'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import { LAST_USED_LOGIN_METHOD_LABEL, LastUsedLoginMethod } from 'lib/auth/lastUsedLoginMethod'

const Container = styled.div`
  position: relative;
`

const LastUsedTag = styled(StatusLabel)`
  display: inline-flex;
  align-items: center;
  margin-left: ${rem(8)};
  pointer-events: none;
`

const SwappablePhoneInput = styled(PhoneInput)`
  && > * {
    transition: none;
  }

  &[hidden] {
    display: none;
  }
`

const SwappableEmailInput = styled(TextInput)`
  && > * {
    transition: none;
  }

  &[hidden] {
    display: none;
  }
`

type InputMode = 'emailOrPhone' | 'phone' | 'email'

interface Props {
  phoneInputName?: string
  emailInputName?: string
  className?: string
  noValidationSpacing?: boolean
  autoFocus?: boolean
  lastUsedLoginMethod?: LastUsedLoginMethod
  inputMode?: InputMode
}

function AccountAccessUserInput(props: Props) {
  const {
    emailInputName = 'email',
    phoneInputName = 'phoneNumber',
    className,
    noValidationSpacing,
    autoFocus = true,
    lastUsedLoginMethod,
    inputMode = 'emailOrPhone',
  } = props

  const [value, setValue] = useState<string>('')
  const emailInputRef = useRef<HTMLInputElement>(null)
  const phoneInputRef = useRef<HTMLInputElement>(null)
  const isMobile = useScreenSizeOnly('mobile')
  const headlessMode = useAppSelector(state => state.system.headlessMode)
  const allowPhoneNumberLogin = useAppSelector(state => !!state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.allowPhoneNumberLogin])
  const [anyInputFocus, setAnyInputFocus] = useState<boolean>(false)
  const onTextChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>((e) => {
    setValue(e.currentTarget.value)
  }, [])

  const resolvedMode: InputMode = useMemo(() => {
    if (!allowPhoneNumberLogin && inputMode !== 'email') {
      return 'emailOrPhone'
    }
    return inputMode
  }, [allowPhoneNumberLogin, inputMode])

  useEffect(() => {
    if (headlessMode || isMobile || !autoFocus) {
      return
    }
    if (resolvedMode === 'phone') {
      phoneInputRef.current?.focus()
      return
    }
    emailInputRef.current?.focus()
  }, [autoFocus, headlessMode, isMobile, resolvedMode])

  const onFocusAnyInput = useCallback(() => {
    setAnyInputFocus(true)
  }, [])

  const isPossiblyPhoneNumber = useMemo(() => {
    if (resolvedMode !== 'emailOrPhone') return resolvedMode === 'phone'
    return allowPhoneNumberLogin && /^(\d|\+)\d*$/.test(value)
  }, [allowPhoneNumberLogin, resolvedMode, value])

  useLayoutEffect(() => {
    if (!anyInputFocus) return
    if (isPossiblyPhoneNumber) {
      phoneInputRef.current?.focus()
    } else {
      emailInputRef.current?.focus()
    }
  }, [anyInputFocus, isPossiblyPhoneNumber])

  const labelBase = useMemo(() => {
    if (resolvedMode === 'phone') return 'Phone number'
    if (resolvedMode === 'email') return 'Email address'
    return 'Email address or phone number'
  }, [resolvedMode])

  const shouldShowLastUsedTag = useMemo(() => {
    if (!lastUsedLoginMethod) return false
    if (resolvedMode === 'phone') return lastUsedLoginMethod === 'phone' || lastUsedLoginMethod === 'otp'
    if (resolvedMode === 'email') return lastUsedLoginMethod === 'email'
    return lastUsedLoginMethod === 'email' || lastUsedLoginMethod === 'phone' || lastUsedLoginMethod === 'otp'
  }, [lastUsedLoginMethod, resolvedMode])

  const labelWithTag = shouldShowLastUsedTag ? <>
    {labelBase}
    <LastUsedTag variant="confirmation">{LAST_USED_LOGIN_METHOD_LABEL}</LastUsedTag>
  </> : labelBase

  if (resolvedMode === 'email') {
    return <TextInput
      label={labelWithTag}
      id="email"
      required
      type="email"
      name="email"
      autoComplete="email"
      placeholder="Email"
      requiredErrorMessage="Email is required"
      ref={emailInputRef}
      helpText="We'll check if you have an account with us or if we need to create one for you."
      className={className}
      noValidationSpacing={noValidationSpacing}
    />
  }

  if (resolvedMode === 'phone') {
    return <SwappablePhoneInput
      className={className}
      inputRef={phoneInputRef}
      label={labelWithTag}
      name={phoneInputName}
      placeholder="Phone number"
      required
      phoneNumber={value}
      onNumberChange={onTextChange}
      onFocus={onFocusAnyInput}
      requiredErrorMessage="Phone number is required"
      noValidationSpacing={noValidationSpacing}
      helpText="We'll check if you have an account with us or if we need to create one for you."
    />
  }

  return <Container className={className}>
    <SwappableEmailInput
      label={labelWithTag}
      id="email"
      required
      type="email"
      name={emailInputName}
      autoComplete="email"
      placeholder="Email or phone number"
      requiredErrorMessage="Email or phone number is required"
      ref={emailInputRef}
      value={value}
      onChange={onTextChange}
      helpText="We'll check if you have an account with us or if we need to create one for you."
      hidden={isPossiblyPhoneNumber}
      disabled={isPossiblyPhoneNumber}
      onFocus={onFocusAnyInput}
      noValidationSpacing={noValidationSpacing}
    />
    <SwappablePhoneInput
      inputRef={phoneInputRef}
      label="Email address or phone number"
      name={phoneInputName}
      placeholder="Email or phone number"
      requiredErrorMessage="Email or phone number is required"
      required
      phoneNumber={value}
      onNumberChange={onTextChange}
      hidden={!isPossiblyPhoneNumber}
      disabled={!isPossiblyPhoneNumber}
      onFocus={onFocusAnyInput}
      noValidationSpacing={noValidationSpacing}
      helpText="We'll check if you have an account with us or if we need to create one for you."
    />
  </Container>
}

export default AccountAccessUserInput
